// The invite form inside the invite panel (InviteDropdown owns its state).
//
//   Email      AppField + AppInput — a labelled field at the panel's full
//              width, an inline error under it in a reserved box
//   Actions    role select (AppSelect, the rest of the row) · copy-link icon
//              (its box reserved, shown once an invitation has a link) ·
//              "Send invite" (the ink capsule; "Sending…" while busy, same
//              width — AppLabelStack)
//   Note       one status line: the result of the last send, in a reserved
//              box (role="status", so it is announced)
//
// ONE SEND = ONE INVITATION. The copy-link icon copies the `inviteUrl` the
// SAME createOrgInvitation call returned; it never creates a second
// invitation (the old magic-link button did, and also sent a second email).
// A project invite returns no link, so the icon stays hidden there.
//
// The context decides the call, never the fields: inside a project
// (root.projectId) the form sends a PROJECT invite, at the role the select
// shows — its default included (before, an untouched select left `role`
// empty and the submit fell through to an ORG invitation). Outside a project
// it sends an org invitation. The roles offered and the default come from
// `invitePolicy` (shared/functions/inviteRoles.js), which mirrors the server.
export const InviteFormInDropdown = {
  tag: 'form',
  flow: 'y',
  gap: 'Z',
  // Validation is ours (inline, in the field's reserved error line), not the
  // browser's bubble.
  noValidate: true,
  hide: (el, s) => !el.call('invitePolicy', s).mayInvite,

  onSubmit: async (ev, el, s) => {
    ev.preventDefault()
    if (!s || s.status === 'sending') return
    const policy = el.call('invitePolicy', s)
    if (!policy.mayInvite) return
    const email = String(s.email || '').trim()
    // The server's own email shape (ProjectMemberController.inviteMember).
    const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/
    if (!email) {
      s.update({ error: 'members.inviteForm.needEmail.message' })
      return
    }
    if (!EMAIL_RE.test(email)) {
      s.update({ error: 'members.inviteForm.invalidEmail' })
      return
    }
    const allowed = policy.inviteRoles.map((r) => r.value)
    const role = allowed.includes(s.role) ? s.role : policy.defaultRole
    const root = s.root || el.getRootState() || {}

    s.update({
      status: 'sending',
      error: '',
      failure: '',
      sentEmail: '',
      sentUrl: '',
      sentNoEmail: false,
      copied: false
    })
    try {
      let url = ''
      let emailSent = true
      if (policy.context === 'project') {
        await el.sdk.inviteMember(root.projectId, email, role)
      } else {
        const orgId = root.jwtClaims && root.jwtClaims.workspace_org_id
        const invite = await el.sdk.createOrgInvitation(orgId, { email, role })
        url = (invite && invite.inviteUrl) || ''
        emailSent = !(invite && invite.emailSent === false)
      }
      s.update({
        status: 'idle',
        email: '',
        sentEmail: email,
        sentUrl: url,
        sentNoEmail: !emailSent
      })
      // Ready for the next address.
      const input = el.AppField && el.AppField.AppInput
      if (input && input.node && typeof input.node.focus === 'function') input.node.focus()
    } catch (err) {
      console.warn('[InviteFormInDropdown] invite failed:', err)
      const failed = el.call('polyglot', 'members.invite.failed')
      s.update({
        status: 'idle',
        failure: el.call('userFacingErrorMessage', err, {
          networkMessage: el.call('polyglot', 'members.invite.offline'),
          fallback: (e) => {
            const msg = String((e && e.message) || '').trim()
            return msg ? failed + ': ' + msg : failed
          }
        })
      })
    }
  },

  AppField: {
    label: (el) => el.call('polyglot', 'members.inviteForm.emailLabel'),
    reserveError: true,
    AppInput: {
      type: 'email',
      name: 'email',
      autocomplete: 'email',
      inputmode: 'email',
      spellcheck: false,
      placeholder: (el) => el.call('polyglot', 'members.inviteForm.placeholder'),
      value: (el, s) => s.email || '',
      ariaInvalid: (el, s) => (s.error ? 'true' : null),
      class: { hasError: (el, s) => !!s.error },
      // Every keystroke, the empty string included: a cleared field must
      // clear the model too (it used to keep the last non-empty address).
      onInput: (ev, el, s) => {
        s.update({ email: ev.target.value, error: '' })
      }
    },
    FieldError: {
      text: (el, s) => (s.error ? el.call('polyglot', s.error) : ''),
      visibility: (el, s) => (s.error ? 'visible' : 'hidden')
    }
  },

  // One line where it fits; on a narrow panel (a phone, a long Georgian role
  // name) the buttons wrap under the select instead of the select clipping
  // its label — a control never clips its own label (design call
  // 2026-08-19: "ადმინისტრატორი" is 186px at this size).
  Actions: {
    flow: 'x',
    flexWrap: 'wrap',
    align: 'center',
    gap: 'Z',
    AppSelect: {
      name: 'role',
      // At least its longest option, growing into the free space; never
      // squeezed below its label.
      flex: '1 0 auto',
      width: 'auto',
      aria: { label: (el) => el.call('polyglot', 'members.inviteForm.roleOfMember') },
      value: (el, s) => {
        const p = el.call('invitePolicy', s)
        return p.inviteRoles.some((r) => r.value === s.role) ? s.role : p.defaultRole
      },
      onChange: (ev, el, s) => {
        s.update({ role: ev.target.value })
      },
      childrenAs: 'props',
      childProps: {
        tag: 'option',
        // `invitePolicy` hands over polyglot KEYS: a '{{ … | polyglot }}'
        // string a factory returns is data and would render raw.
        text: (el) => el.call('polyglot', el.labelKey),
        attr: {
          selected: (el, s) => {
            const p = el.call('invitePolicy', s)
            const current = p.inviteRoles.some((r) => r.value === s.role) ? s.role : p.defaultRole
            return el.value === current ? '' : null
          }
        }
      },
      children: (el, s) => el.call('invitePolicy', s).inviteRoles
    },

    // The link of the invitation just sent. Its box is always there (Rule
    // 66 — nothing moves when it appears); hidden, it is out of the tab
    // order and the accessibility tree.
    CompactIconButton: {
      // Pushes itself and the send button to the row's end — on the select's
      // line, or on their own line when the row wraps.
      marginLeft: 'auto',
      icon: 'copy outline',
      aria: {
        label: (el) => el.call('polyglot', 'members.inviteDropdown.copyLink'),
        hidden: (el, s) => (s.sentUrl ? null : 'true')
      },
      title: (el) => el.call('polyglot', 'members.inviteDropdown.copyLink'),
      visibility: (el, s) => (s.sentUrl ? 'visible' : 'hidden'),
      tabindex: (el, s) => (s.sentUrl ? null : '-1'),
      onClick: (ev, el, s) => {
        ev.preventDefault()
        if (!s.sentUrl) return
        el.call('copyStringToClipboard', s.sentUrl)
        s.update({ copied: true })
      }
    },

    AppCapsuleBtnPrimary: {
      type: 'submit',
      flexShrink: '0',
      disabled: (el, s) => s.status === 'sending',
      ariaBusy: (el, s) => (s.status === 'sending' ? 'true' : null),
      AppLabelStack: {
        labels: ['members.inviteForm.send', 'members.inviteForm.sending'],
        shown: (el, s) => (s.status === 'sending' ? 1 : 0)
      }
    }
  },

  Note: {
    tag: 'p',
    role: 'status',
    margin: '0',
    minHeight: 'Z1',
    fontSize: 'Y1',
    color: (el, s) => (s.failure ? 'dangerInk' : 'caption'),
    text: (el, s) => {
      if (s.failure) return s.failure
      if (s.copied) return el.call('polyglot', 'members.inviteDropdown.linkCopied.title')
      if (!s.sentEmail) return ''
      const key = s.sentNoEmail ? 'members.inviteDropdown.sentNoEmail' : 'members.inviteSent.person'
      return el.call('polyglot', key).replace('{who}', s.sentEmail).replace('{email}', s.sentEmail)
    }
  }
}
